// Layer 3 — the Value and Learn stage bodies under the Advisory strip
// (polish.P28c, #715; comp `.impeccable/mocks/decision/advisory-stages.html`,
// family A with C's partition and B's expand on Value and B's learned row on
// Learn). Pure projections: no React, no engine mutations.

import type { Persona } from '@/shared/types';
import type { FMEAUpdateReview, ReviewSeat } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { approvalTabForReview, liveRuleSet, ruleSetCounts } from '@/versions/v1/shared/RuleSetEngine';
import { rulingStateLabel, type ApprovalsRuleSetPanel } from './Approvals.transform';
import { flowStamp, type FlowCapturedRow, type FlowLearnRow } from './FlowLanding.transform';
import { formatDailyA } from './ValueBand.transform';

// ---------------------------------------------------------------------------
// Value · Captured this shift (the 4)
// ---------------------------------------------------------------------------

/**
 * The Captured panel's head names its own list, never the Value cell's money
 * (comp binding list): `8 resolved · 1 priced`. A seat that reads no US$ is
 * not told how many rows carry a price it cannot see.
 */
export function capturedPanelMeta(rows: readonly FlowCapturedRow[], showMoney: boolean): string {
  const resolved = `${rows.length} resolved`;
  if (!showMoney) return resolved;
  const priced = rows.filter((row) => row.dailyLossPerDay !== null).length;
  return `${resolved} · ${priced} priced`;
}

export type CapturedRowView = {
  caseId: string;
  title: string;
  stamp: string;
  /** Money seats on a priced row: the captured US$/day. */
  money: string | null;
  /** Under the money, or alone: what the row fed. */
  comparison: string;
};

/** One captured row: the figure and `captured · <node>`, or `Feeds <node>` for
 *  an unpriced row and for every row a floor seat reads (comp adjudication 7). */
export function capturedRowView(row: FlowCapturedRow, showMoney: boolean): CapturedRowView {
  const priced = showMoney && row.dailyLossPerDay !== null;
  return {
    caseId: row.caseItem.id,
    title: row.caseItem.title,
    stamp: `${formatDayMonth(row.at)} ${row.at.slice(11, 16)}`,
    money: priced ? formatDailyA(row.dailyLossPerDay!) : null,
    comparison: priced ? `captured · ${row.feedLabel}` : `Feeds ${row.feedLabel}`,
  };
}

// ---------------------------------------------------------------------------
// Learn · Updated this week (top of the 4)
// ---------------------------------------------------------------------------

export type LearnedRowView = { id: string; summary: string; meta: string };

/** A seat label without its extension: `Plant Manager (Ext 2100)` → `Plant Manager`. */
function seatName(label: string): string {
  return label.replace(/\s*\(.*\)\s*$/, '');
}

/**
 * B's learned row: the approved change over its code, the approval clock and
 * the approver (polish.P28c r1: the approval is credited to who approved it,
 * never to the earlier reviewer). An approved review is live in the rule set
 * the chip names, so its line reads that one version (comp adjudication 8).
 */
export function learnedRowView(row: FlowLearnRow, liveVersion: number): LearnedRowView {
  const clock = `${formatDayMonth(row.at)} ${row.at.slice(11, 16)}`;
  const live = row.source === 'review' ? ` · live in rule set v${liveVersion}` : '';
  const code = row.code ?? row.detail.split(' · ')[0];
  const approver = row.approvedBy ? ` · ${seatName(row.approvedBy)}` : '';
  return { id: row.id, summary: row.summary, meta: `${code} · approved ${clock}${approver}${live}` };
}

/** A review event's clock on the case clock: `08 Jun 09:15`. */
export function eventClock(stamp: string, cursor: SeriesTime): string {
  const at = flowStamp(stamp, cursor);
  return at ? `${formatDayMonth(at)} ${at.slice(11, 16)}` : stamp;
}

/** The table's clock: the time alone on the cursor's day (comp A: `reviewed by
 *  Met 09:15`), the day and time before it. */
function tableClock(stamp: string, cursor: SeriesTime): string {
  const at = flowStamp(stamp, cursor);
  if (!at) return stamp;
  return at.slice(0, 10) === cursor.slice(0, 10) ? at.slice(11, 16) : `${formatDayMonth(at)} ${at.slice(11, 16)}`;
}

/**
 * The proposals table's State cell (polish.P28c r1). Every time names its
 * event: `reviewed by Met 09:15 · awaiting GM` while the gate is open;
 * `reviewed 09:00 · L. Chen · approved 09:15` once approved. A proposal still
 * with its seat reads `routed · awaiting review`: the Review column already
 * names the seat, so the routing is said once.
 */
export function learnStateLabel(review: FMEAUpdateReview, cursor: SeriesTime, liveVersion: number): string {
  if (review.reviewedBy && review.reviewedAt && review.reviewStatus === 'approved' && review.approvalStatus === 'pending') {
    return `reviewed by Met ${tableClock(review.reviewedAt, cursor)} · awaiting ${review.approvalLabel ?? 'GM'}`;
  }
  if (review.approvalStatus === 'pending' && review.reviewStatus === 'pending') return 'routed · awaiting review';
  if (review.approvalStatus === 'pending' && review.reviewStatus !== 'rejected') return rulingStateLabel(review, cursor, liveVersion);
  const reviewed = review.reviewedBy
    ? `reviewed ${review.reviewedAt ? `${tableClock(review.reviewedAt, cursor)} · ` : ''}${review.reviewedBy}`
    : null;
  let decided: string | null = null;
  if (review.approvalStatus === 'approved' && review.approvedAt) {
    decided = `approved ${tableClock(review.approvedAt, cursor)}`;
  } else if (review.approvalStatus === 'rejected' || review.reviewStatus === 'rejected') {
    decided = 'rejected';
  }
  const parts = [reviewed, decided].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : rulingStateLabel(review, cursor, liveVersion);
}

// ---------------------------------------------------------------------------
// Learn · the proposals table (the 8) and the decision (the 4)
// ---------------------------------------------------------------------------

export type RuleSetRead = { version: number; live: number; proposed: number };

/** The live rule set the Learn head, tabs and learned line all read. */
export function learnRuleSetRead(advisory: AdvisorySessionState): RuleSetRead {
  const counts = ruleSetCounts(advisory);
  return { version: liveRuleSet(advisory).version.n, live: counts.live, proposed: counts.proposed };
}

/** Tab headlines: each tab's own row count, zero included (`Rejected 0`). */
export function approvalTabCounts(reviews: readonly FMEAUpdateReview[]): Record<'to-review' | 'approved' | 'rejected', string> {
  const count = (tab: 'to-review' | 'approved' | 'rejected') =>
    String(reviews.filter((review) => approvalTabForReview(review) === tab).length);
  return { 'to-review': count('to-review'), approved: count('approved'), rejected: count('rejected') };
}

const SEAT_SHORT: Record<ReviewSeat, string> = {
  'senior-metallurgist': 'Senior Met',
  'senior-reliability-engineer': 'Sr Reliability Eng.',
};

/** The review column's second line: the seat the proposal is routed to, short. */
export function reviewSeatShort(review: FMEAUpdateReview): string {
  return review.reviewSeats.map((seat) => SEAT_SHORT[seat] ?? seat).join(' · ');
}

/** The state cell: the ruling so far, then what it waits on in ink-secondary. */
export function reviewStateParts(label: string): { lead: string; awaiting: string | null } {
  const at = label.lastIndexOf(' · awaiting ');
  if (at < 0) return { lead: label, awaiting: null };
  return { lead: `${label.slice(0, at)} · `, awaiting: label.slice(at + 3) };
}

/** A `·`-joined meta line as its phrases, so a line breaks between phrases and
 *  never inside one (`L. Chen`, `rule set v8`; polish.P28c r1). */
export function metaPhrases(text: string): string[] {
  return text.split(' · ').filter((phrase) => phrase.length > 0);
}

/** The seat a gate's Approve names: the review's own persona seat, else the default gate. */
export function approveButtonLabel(review: FMEAUpdateReview, persona: Persona, defaultGate: string): string {
  if (review.approvalPersonas?.includes(persona)) return `Approve · ${persona}`;
  return `Approve · ${defaultGate.replace(/\s*\(.*\)\s*$/, '')}`;
}

/** The seat a pending gate waits on, read as `Final gate · <seat>` by a seat that cannot approve. */
export function finalGateSeat(review: FMEAUpdateReview, defaultGate: string): string {
  return review.approvalLabel ?? defaultGate;
}

/** The gate's state pill: the draft until approved, then the approval stamp. */
export function gatePillText(review: FMEAUpdateReview): { tone: 'draft' | 'approved' | 'rejected'; text: string } {
  if (review.approvalStatus === 'approved') {
    const at = review.approvedAt ? review.approvedAt.slice(11, 16) : '';
    return { tone: 'approved', text: ['Approved', at, review.approvedBy?.replace(/\s*\(.*\)\s*$/, '')].filter(Boolean).join(' · ') };
  }
  if (review.approvalStatus === 'rejected' || review.reviewStatus === 'rejected') return { tone: 'rejected', text: 'Rejected' };
  return { tone: 'draft', text: 'Draft — not live until approved' };
}

/** A floor seat reads no US$ on Learn either: the money basis row drops. */
export function approvalBasisForSeat(
  basis: readonly { label: string; value: string; money?: boolean }[] | undefined,
  showMoney: boolean,
): readonly { label: string; value: string }[] {
  return (basis ?? []).filter((item) => showMoney || !item.money);
}

// ---------------------------------------------------------------------------
// Learn · the full-width card's tabs
// ---------------------------------------------------------------------------

export type LearnTabId = 'rules' | 'changes' | 'session';

/** Each tab's label carries its headline: `Rule set v7 · 51 live`, `Changes v6 → v7 · 3 added`. */
export function learnTabs(
  ruleSet: RuleSetRead,
  panel: Pick<ApprovalsRuleSetPanel, 'fromVersion' | 'toVersion' | 'rows'>,
  sessionUpdates: number,
): { id: LearnTabId; label: string; count?: string }[] {
  const added = panel.rows.filter((row) => row.kind === 'added').length;
  const changed = panel.rows.filter((row) => row.kind === 'changed').length;
  const changeCount = [added ? `${added} added` : '', changed ? `${changed} changed` : ''].filter(Boolean).join(' · ');
  return [
    { id: 'rules', label: `Rule set v${ruleSet.version}`, count: `${ruleSet.live} live` },
    ...(panel.rows.length > 0
      ? [{ id: 'changes' as const, label: `Changes v${panel.fromVersion} → v${panel.toVersion}`, count: changeCount }]
      : []),
    ...(sessionUpdates > 0
      ? [{ id: 'session' as const, label: 'FMEA updates this session', count: String(sessionUpdates) }]
      : []),
  ];
}

/** The KPI / symptom block: the case's observation when it already names the
 *  title (`SMBS carry-over · Zn pH low`), else the title over the observation. */
export function symptomLines(caseItem: { title: string; observation?: string }): { lead: string; detail: string | null } {
  const observation = caseItem.observation?.trim();
  if (!observation || observation === caseItem.title) return { lead: caseItem.title, detail: null };
  if (observation.startsWith(caseItem.title)) return { lead: observation, detail: null };
  return { lead: caseItem.title, detail: observation };
}
